import { WA_WINDOW } from '@ng-web-apis/common';
import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IsLoadingService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestAddressesService } from '@core/services/request/request-addresses.service';
import { RequestService } from '@core/services/request/request.service';
import { WorkflowFormSyncCoordinatorService } from '@core/services/workflow';
import {
  BehaviorSubject,
  distinctUntilChanged,
  merge,
  Subject,
  switchMap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestDTO } from '@core/types';
import {
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiInput,
  TuiTextfield,
} from '@taiga-ui/core';
import { TuiInputInline, TuiSkeleton } from '@taiga-ui/kit';
import { TranslatePipe } from '@ngx-translate/core';
import { IconButtonComponent } from '@common/components';

enum FormFields {
  ActualDeliveryAddress = 'actualDeliveryAddress',
}

type FormType = Pick<RequestDTO, 'actualDeliveryAddress'>;

@Component({
  selector: 'x-request-form-actual-address',
  imports: [
    TuiSkeleton,
    TuiTextfield,
    CommonModule,
    FormsModule,
    TuiButton,
    TuiInput,
    ReactiveFormsModule,
    TuiIcon,
    TranslatePipe,
    IconButtonComponent,
    TuiInputInline,
    TuiHint,
  ],
  templateUrl: './request-form-actual-address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormActualAddressComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WA_WINDOW);
  private readonly requestService = inject(RequestService);
  private readonly requestAddressesService = inject(RequestAddressesService);
  private readonly syncCoordinator = inject(
    WorkflowFormSyncCoordinatorService,
    { optional: true },
  );
  readonly permissionService = inject(OutkeepPermissionService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly FormFields = FormFields;
  readonly setActualDeliveryAddress$ = new Subject<void>();
  readonly resetActualDeliveryAddress$ = new Subject<void>();
  readonly $request = this.requestService.$request;
  formGroup = this.formBuilder.group<FormType>({
    [FormFields.ActualDeliveryAddress]: '',
  });
  $isInsideDealDetails = input<boolean>(false, {
    alias: 'isInsideDealDetails',
  });

  $isInsideDesktopSummary = input<boolean>(false, {
    alias: 'isInsideDesktopSummary',
  });

  $isInsideWorkflow = input<boolean>(false, {
    alias: 'isInsideWorkflow',
  });

  $canExecuteWorkflow = input<boolean>(true, {
    alias: 'canExecuteWorkflow',
  });

  /**
   * Флаг выполнил ли пользователь проверку введенного им адреса путём перехода
   * на яндекс карту.
   */
  isEnteredActualDeliveryAddressVerified = false;

  constructor() {
    effect(() => {
      if (this.$request().deliveryAddress) {
        this.patchDeliveryAddressForVerify();
      }
    });

    // Register handler for workflow forms only (when inside workflow)
    effect(() => {
      const isInsideWorkflow = this.$isInsideWorkflow();

      if (isInsideWorkflow) {
        this.syncCoordinator?.registerHandler({
          handlerId: 'actualAddressUpdate',
          handler: async () => {
            // If user has entered and verified address, trigger update and wait for completion
            if (
              this.isEnteredActualDeliveryAddressVerified &&
              this.actualDeliveryAddressToSet.value
            ) {
              // Trigger the update and convert observable to promise to properly await
              await new Promise<void>((resolve, reject) => {
                this.performSettingActualDeliveryAddress$().subscribe({
                  next: () => resolve(),
                  error: err =>
                    reject(err instanceof Error ? err : new Error(String(err))),
                });
              });
            }
          },
        });
      }
    });
  }

  ngOnInit() {
    this.initUnVerifyAddressWhenFormValueChanges();
    this.initUpdateActualDeliveryAddressEventHandler();
  }

  private patchDeliveryAddressForVerify() {
    this.formGroup.patchValue({
      actualDeliveryAddress: this.$request().deliveryAddress,
    });
  }

  private initUnVerifyAddressWhenFormValueChanges() {
    this.actualDeliveryAddressToSet.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.isEnteredActualDeliveryAddressVerified = false));
  }

  private initUpdateActualDeliveryAddressEventHandler() {
    merge(
      this.setActualDeliveryAddress$.pipe(
        switchMap(() => this.performSettingActualDeliveryAddress$()),
      ),
      this.resetActualDeliveryAddress$.pipe(
        switchMap(() => this.performResettingActualDeliveryAddress$()),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.actualDeliveryAddressToSet.removeValidators(Validators.required);
        this.actualDeliveryAddressToSet.reset();
        this.requestService.reloadRequest();
      });
  }

  private performSettingActualDeliveryAddress$() {
    return this.requestAddressesService.setActualDeliveryAddress$(
      this.actualDeliveryAddressToSet.value!,
    );
  }

  private performResettingActualDeliveryAddress$() {
    return this.requestAddressesService.resetActualDeliveryAddress$();
  }

  onCheckAddressButtonClick() {
    this.isEnteredActualDeliveryAddressVerified = true;
  }

  isDeliveryAddressDifferentFromActual() {
    return this.requestAddressesService.isDeliveryAddressDifferentFromActual();
  }

  @pure
  get actualDeliveryAddressToSet() {
    return this.formGroup.controls[FormFields.ActualDeliveryAddress]!;
  }

  resetActualDeliveryAddress() {
    this.resetActualDeliveryAddress$.next();
  }

  openMapToVerifyAddress() {
    this.window.open(
      `https://maps.yandex.ru/?text=${this.actualDeliveryAddressToSet.value}&l=sat%2Cskl`,
      '_blank',
    );
    this.isEnteredActualDeliveryAddressVerified = true;
  }
}
